import { createResource, createSignal, For, Show } from "solid-js";
import type { PlatformEvent } from "@energyplatform/client";
import { api } from "../services.ts";
import { t } from "../i18n.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { Dialog, useAction } from "./ui.tsx";

const CAUSES = [
  "fixed",
  "expected",
  "falseAlarm",
  "deviceRepaired",
  "communicationRestored",
  "other",
] as const;

/** Assigns the tasks of events to a person who has signed in, with a due date and a comment (prototype "Przydziel zadanie"). */
export function AssignDialog(props: {
  events: PlatformEvent[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [users] = createResource(() => api.events.users());
  const [who, setWho] = createSignal("");
  const [due, setDue] = createSignal("");
  const [note, setNote] = createSignal("");
  const [run, error, busy] = useAction();
  const save = () =>
    void run(async () => {
      await api.events.task(
        props.events.map((e) => e.id),
        { assignee: who(), due: due() || null, note: note().trim() || null },
      );
      await props.onDone();
      props.onClose();
    });
  return (
    <Dialog
      title={
        props.events.length === 1
          ? t("events.assignTitleOne")
          : t("events.assignTitle", { count: props.events.length })
      }
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !who()}
            onClick={save}
          >
            {t("events.assign")}
          </button>
        </>
      }
    >
      <div class="stack">
        <div class="g-2e grid" style={{ gap: "10px" }}>
          <div class="field">
            <label for="eaWho">{t("events.assignWho")}</label>
            <select
              id="eaWho"
              value={who()}
              onChange={(e) => setWho(e.currentTarget.value)}
            >
              <option value="" disabled>
                {t("events.assignPick")}
              </option>
              <For each={users() ?? []}>
                {(u) => <option value={u.id}>{u.name}</option>}
              </For>
            </select>
          </div>
          <div class="field">
            <label for="eaDue">{t("events.due")}</label>
            <input
              id="eaDue"
              type="date"
              value={due()}
              onInput={(e) => setDue(e.currentTarget.value)}
            />
          </div>
        </div>
        <div class="field">
          <label for="eaNote">{t("events.assignComment")}</label>
          <textarea
            id="eaNote"
            rows="3"
            maxLength={2000}
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
          />
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t("events.assignHint")}
        </p>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

/** Closes the tasks with a cause and what was done; the condition itself is not touched (prototype "Zamknij zadanie"). */
export function CloseDialog(props: {
  events: PlatformEvent[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [cause, setCause] = createSignal<(typeof CAUSES)[number]>("fixed");
  const [text, setText] = createSignal("");
  const [run, error, busy] = useAction();
  const live = () => props.events.filter((e) => e.endedAt === null).length;
  const save = () =>
    void run(async () => {
      const note = [t(`events.cause.${cause()}`), text().trim()]
        .filter(Boolean)
        .join(" — ");
      await api.events.task(
        props.events.map((e) => e.id),
        { stage: "closed", note },
      );
      await props.onDone();
      props.onClose();
    });
  return (
    <Dialog
      title={
        props.events.length === 1
          ? t("events.closeTitleOne")
          : t("events.closeTitle", { count: props.events.length })
      }
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy()}
            onClick={save}
          >
            {t("events.closeTask")}
          </button>
        </>
      }
    >
      <div class="stack">
        <div class="field">
          <label for="ecCause">{t("events.cause.label")}</label>
          <select
            id="ecCause"
            value={cause()}
            onChange={(e) =>
              setCause(e.currentTarget.value as (typeof CAUSES)[number])
            }
          >
            <For each={CAUSES}>
              {(c) => <option value={c}>{t(`events.cause.${c}`)}</option>}
            </For>
          </select>
        </div>
        <div class="field">
          <label for="ecText">{t("events.closeText")}</label>
          <textarea
            id="ecText"
            rows="3"
            maxLength={2000}
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        </div>
        <Show when={live() > 0}>
          <div
            class="note"
            style={{
              margin: 0,
              border: 0,
              "border-radius": "6px",
              color: "var(--warn)",
            }}
          >
            <Icon name="alert" />
            {t("events.closeLive", {
              count: live(),
              total: props.events.length,
            })}
          </div>
        </Show>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}
